import type { FileAssignmentRecord } from '../../../../domain/src/lib/repository/file-assignment.model';
import {
  readVaultOperativeRole,
  readVaultUserDataFromLocalStorage,
  resolveVaultActiveTenantId,
  userIsAdminEmpresaFromSession,
  userIsSuperadminFromSession,
} from '@nebula/shared-vault/data-access';
import {
  getParentRepositoryExplorerPath,
  getParentZipInternalPath,
  isZipArchiveAssignmentPath,
  normalizeAssignmentScopePath,
  normalizeRepositoryExplorerPath,
  normalizeZipInternalPath,
  resolveRepositoryDeepLinkListPath,
  resolveRepositoryItemPath,
  splitZipVirtualAssignmentPath,
} from './repository-assignment-path.util';
export interface RepositoryListItemLike {
  name: string;
  path?: string | null;
  type?: string | null;
  internal_path?: string | null;
}

function normalizeRepoAssignmentPath(path: string | null | undefined): string {
  return normalizeAssignmentScopePath(path);
}

function normalizeUserId(value: string | number | null | undefined): string {
  return String(value ?? '')
    .trim()
    .replace(/-/g, '')
    .toLowerCase();
}

function normalizeAssignmentRoleToken(role: string | null | undefined): string {
  const norm = (role ?? '').trim().toLowerCase().replace(/[\s_-]+/g, '');
  if (norm === 'consultor') {
    return 'consulta';
  }
  return norm;
}

/** Rol operativo en sesión (vault_operative_role o tenant activo en user_data). */
export function resolveSessionOperativeRoleForAssignments(
  override?: string | null,
): string {
  const explicit = normalizeAssignmentRoleToken(override ?? readVaultOperativeRole() ?? '');
  if (explicit) {
    return explicit;
  }
  const u = readVaultUserDataFromLocalStorage();
  if (!u) {
    return '';
  }
  const tenantId = resolveVaultActiveTenantId();
  const tenants = u['tenants'];
  if (!Array.isArray(tenants)) {
    return '';
  }
  for (const entry of tenants) {
    if (!entry || typeof entry !== 'object') {
      continue;
    }
    const row = entry as Record<string, unknown>;
    const tid = String(row['tenant_id'] ?? row['tenantId'] ?? '')
      .trim()
      .toLowerCase();
    const role = normalizeAssignmentRoleToken(String(row['role'] ?? ''));
    if (!role) {
      continue;
    }
    if (!tenantId || tid === tenantId.toLowerCase()) {
      return role;
    }
  }
  return '';
}

/** Todos los ids posibles del usuario en sesión (JWT / user_data pueden diferir del id de assign). */
export function collectSessionUserIdCandidates(primaryUserId?: string | null): string[] {
  const out: string[] = [];
  const push = (raw: unknown) => {
    if (raw == null) {
      return;
    }
    const normalized = normalizeUserId(String(raw));
    if (!normalized || out.includes(normalized)) {
      return;
    }
    out.push(normalized);
  };

  push(primaryUserId);
  const u = readVaultUserDataFromLocalStorage();
  if (u) {
    for (const key of ['vault_user_id', 'id', 'user_id', 'sub'] as const) {
      push(u[key]);
    }
  }
  return out;
}

function rowUserIdMatchesSession(
  row: FileAssignmentRecord,
  sessionUserIds: string[],
): boolean {
  const rowUserId = row.user_id != null ? String(row.user_id).trim() : '';
  if (!rowUserId || sessionUserIds.length === 0) {
    return false;
  }
  const normalizedRow = normalizeUserId(rowUserId);
  return sessionUserIds.some((id) => id === normalizedRow);
}

/** Filas por usuario, por rol operativo o globales (sin user_id ni role). */
export function assignmentAppliesToSessionUser(
  row: FileAssignmentRecord,
  userId: string,
  operativeRole?: string | null,
): boolean {
  const sessionUserIds = collectSessionUserIdCandidates(userId);
  if (sessionUserIds.length === 0) {
    return false;
  }
  const rowUserId = row.user_id != null ? String(row.user_id).trim() : '';
  if (rowUserId) {
    return rowUserIdMatchesSession(row, sessionUserIds);
  }
  const rowRole = normalizeAssignmentRoleToken(row.role);
  if (!rowRole) {
    return true;
  }
  const sessionRole = resolveSessionOperativeRoleForAssignments(operativeRole);
  return !!sessionRole && sessionRole === rowRole;
}

function pathTailKey(path: string, segments = 4): string {
  const parts = normalizeRepoAssignmentPath(path).split('/').filter(Boolean);
  if (parts.length === 0) {
    return '';
  }
  return parts.slice(-Math.min(segments, parts.length)).join('/').toLowerCase();
}

function pathsMatchForAssignment(itemPath: string, assignedPath: string): boolean {
  const item = normalizeRepoAssignmentPath(itemPath);
  const assigned = normalizeRepoAssignmentPath(assignedPath);
  if (!item || !assigned) {
    return false;
  }
  if (item === assigned) {
    return true;
  }
  if (item.endsWith(`/${assigned}`) || assigned.endsWith(`/${item}`)) {
    return true;
  }
  return pathTailKey(item) === pathTailKey(assigned);
}

function assignedPathContainsItem(assignedPath: string, itemPath: string): boolean {
  const assigned = normalizeRepoAssignmentPath(assignedPath);
  const item = normalizeRepoAssignmentPath(itemPath);
  if (!assigned || !item) {
    return false;
  }
  if (item === assigned) {
    return true;
  }
  if (assigned.startsWith(`${item}/`)) {
    return true;
  }
  if (item.startsWith(`${assigned}/`)) {
    return true;
  }
  return pathsMatchForAssignment(item, assigned);
}

function itemRepositoryPath(
  item: RepositoryListItemLike,
  insideZip = false,
  listingContextPath = '',
): string {
  if (insideZip) {
    return zipInternalItemPath(item, listingContextPath);
  }
  const listing = normalizeRepoAssignmentPath(listingContextPath);
  const raw = (item.path || item.name || '').trim();
  if (!raw) {
    return '';
  }
  return normalizeRepoAssignmentPath(resolveRepositoryItemPath(raw, listing));
}

function assignmentIsActive(assignment: FileAssignmentRecord, nowMs = Date.now()): boolean {
  if (!assignment.expires_at) {
    return true;
  }
  const ts = new Date(assignment.expires_at).getTime();
  return Number.isFinite(ts) && ts > nowMs;
}

function parentDirectory(path: string): string {
  const norm = normalizeRepoAssignmentPath(path);
  const idx = norm.lastIndexOf('/');
  return idx > 0 ? norm.slice(0, idx) : '';
}

function isZipLikePath(path: string): boolean {
  return isZipArchiveAssignmentPath(path);
}

function zipInternalItemPath(
  item: RepositoryListItemLike,
  listingContextPath = '',
): string {
  const raw = normalizeZipInternalPath(item.internal_path || item.path || item.name);
  const ctx = normalizeZipInternalPath(listingContextPath);
  if (!raw) {
    return ctx;
  }
  if (raw.includes('/') || !ctx) {
    return raw;
  }
  return normalizeZipInternalPath(`${ctx}/${raw}`);
}

function isZipInternalPathWithinAssignment(
  target: string,
  assignedInternal: string,
  isDirectory: boolean,
): boolean {
  const item = normalizeZipInternalPath(target);
  const assigned = normalizeZipInternalPath(assignedInternal);
  if (!item || !assigned) {
    return false;
  }
  if (item === assigned || pathsMatchForAssignment(item, assigned)) {
    return true;
  }
  if (isDirectory && (item.startsWith(`${assigned}/`) || assigned.startsWith(`${item}/`))) {
    return true;
  }
  return false;
}

/** Un .zip nunca se trata como carpeta aunque el API marque is_directory. */
export function isAssignmentDirectoryRecord(assignment: FileAssignmentRecord): boolean {
  const path = normalizeRepoAssignmentPath(assignment.file_path);
  if (!path || isZipLikePath(path)) {
    return false;
  }
  return assignment.is_directory === true;
}

/** Solo superadmin y administrador de empresa ven el repositorio completo sin asignaciones. */
export function hasFullRepositoryBrowseAccess(): boolean {
  if (userIsSuperadminFromSession()) {
    return true;
  }
  return userIsAdminEmpresaFromSession();
}

/**
 * Archivista, auditor y consulta: siempre acotar el listado.
 * Sin asignaciones → lista vacía (alineado con GET /api/repository/list en backend).
 */
export function shouldFilterRepositoryByMyAssignments(
  _assignments: FileAssignmentRecord[],
): boolean {
  if (hasFullRepositoryBrowseAccess()) {
    return false;
  }
  return true;
}
export interface ResolveMyAssignmentsOptions {
  /**
   * true cuando GET /assigned ya se pidió con user_id (o JWT operativo).
   * Si el filtro estricto queda vacío pero no hay filas de otros usuarios, se confía en el API.
   */
  trustApiScope?: boolean;
}

/**
 * Resuelve asignaciones aplicables al usuario desde GET /assigned.
 * Usuarios operativos: el API filtra por JWT/user_id; aquí se defiende user_id + rol operativo.
 */
export function resolveMyFileAssignmentsFromApi(
  assignments: FileAssignmentRecord[],
  userId: string,
  operativeRole?: string | null,
  options?: ResolveMyAssignmentsOptions,
): FileAssignmentRecord[] {
  const sessionUserIds = collectSessionUserIdCandidates(userId);
  if (sessionUserIds.length === 0) {
    return [];
  }
  const active = assignments.filter((row) => assignmentIsActive(row));
  const matched = dedupeAssignments(
    active.filter((row) => assignmentAppliesToSessionUser(row, userId, operativeRole)),
  );
  if (matched.length > 0) {
    return matched;
  }

  // Evita lista vacía cuando el id de sesión no coincide con file_assignments.user_id
  // pero el API ya devolvió el alcance de un solo usuario (GET ?user_id=...).
  if (options?.trustApiScope && active.length > 0) {
    const distinctUserIds = [
      ...new Set(
        active
          .map((row) =>
            row.user_id != null ? normalizeUserId(String(row.user_id).trim()) : '',
          )
          .filter(Boolean),
      ),
    ];
    if (distinctUserIds.length <= 1) {
      return dedupeAssignments(
        active.filter((row) => {
          const rowUserId = row.user_id != null ? String(row.user_id).trim() : '';
          if (rowUserId) {
            return true;
          }
          const rowRole = normalizeAssignmentRoleToken(row.role);
          if (!rowRole) {
            return true;
          }
          const sessionRole = resolveSessionOperativeRoleForAssignments(operativeRole);
          return !!sessionRole && sessionRole === rowRole;
        }),
      );
    }
  }

  return [];
}

function dedupeAssignments(rows: FileAssignmentRecord[]): FileAssignmentRecord[] {
  const seen = new Set<string>();
  const out: FileAssignmentRecord[] = [];
  for (const row of rows) {
    const key = row.id || `${row.file_path}|${row.user_id ?? ''}|${row.role ?? ''}`;
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    out.push(row);
  }
  return out;
}

export function isRepositoryItemAllowedByAssignments(
  item: RepositoryListItemLike,
  assignments: FileAssignmentRecord[],
  insideZip = false,
  listingContextPath = '',
  zipArchivePath = '',
  options?: { ignoreListingEntryGate?: boolean },
): boolean {
  const itemPath = itemRepositoryPath(item, insideZip, listingContextPath);
  const zipContext = normalizeRepoAssignmentPath(zipArchivePath);
  const zipItemPath = insideZip ? zipInternalItemPath(item, listingContextPath) : '';
  /** Hit de búsqueda (u otra ruta virtual) dentro de un ZIP: `…/paquete.zip::data/file.pdf`. */
  const searchZipHit = !insideZip ? splitZipVirtualAssignmentPath(itemPath) : null;
  const ignoreListingEntryGate = Boolean(options?.ignoreListingEntryGate);

  for (const assignment of assignments) {
    if (!assignmentIsActive(assignment)) {
      continue;
    }
    const assignedPath = normalizeRepoAssignmentPath(assignment.file_path);
    if (!assignedPath) {
      continue;
    }
    const zipVirtual = splitZipVirtualAssignmentPath(assignedPath);

    if (zipVirtual) {
      if (insideZip && zipContext && pathsMatchForAssignment(zipContext, zipVirtual.zipPath)) {
        if (isAssignmentDirectoryRecord(assignment)) {
          if (isZipInternalPathWithinAssignment(zipItemPath, zipVirtual.internalPath, true)) {
            return true;
          }
          continue;
        }
        if (isZipInternalPathWithinAssignment(zipItemPath, zipVirtual.internalPath, false)) {
          return true;
        }
        continue;
      }

      if (
        !insideZip &&
        searchZipHit &&
        pathsMatchForAssignment(searchZipHit.zipPath, zipVirtual.zipPath) &&
        isZipInternalPathWithinAssignment(
          searchZipHit.internalPath,
          zipVirtual.internalPath,
          isAssignmentDirectoryRecord(assignment),
        )
      ) {
        return true;
      }

      const zipEntry = getParentRepositoryExplorerPath(zipVirtual.zipPath) || parentDirectory(zipVirtual.zipPath);
      if (
        !insideZip &&
        !ignoreListingEntryGate &&
        zipEntry &&
        !isListingAtAssignmentEntry(listingContextPath, zipEntry)
      ) {
        continue;
      }

      if (!insideZip && isZipLikePath(itemPath) && pathsMatchForAssignment(itemPath, zipVirtual.zipPath)) {
        return true;
      }
      continue;
    }

    if (insideZip && zipContext && isZipLikePath(assignedPath)) {
      if (pathsMatchForAssignment(zipContext, assignedPath)) {
        return true;
      }
    }

    if (
      insideZip &&
      zipContext &&
      isAssignmentDirectoryRecord(assignment) &&
      (assignedPathContainsItem(assignedPath, zipContext) ||
        pathsMatchForAssignment(zipContext, assignedPath))
    ) {
      // Carpeta DIP/CONSULTAS (etc.): el ZIP hijo ya está en alcance → mostrar todo su interior.
      return true;
    }

    if (
      !insideZip &&
      searchZipHit &&
      isZipLikePath(assignedPath) &&
      pathsMatchForAssignment(searchZipHit.zipPath, assignedPath)
    ) {
      return true;
    }

    if (isAssignmentDirectoryRecord(assignment)) {
      if (assignedPathContainsItem(assignedPath, itemPath)) {
        return true;
      }
      if (searchZipHit && assignedPathContainsItem(assignedPath, searchZipHit.zipPath)) {
        return true;
      }
      continue;
    }

    const entryPath = resolveAssignmentEntryListPath(assignment);
    if (
      !insideZip &&
      !ignoreListingEntryGate &&
      entryPath &&
      !isListingAtAssignmentEntry(listingContextPath, entryPath)
    ) {
      continue;
    }

    if (pathsMatchForAssignment(itemPath, assignedPath)) {
      return true;
    }
  }

  return false;
}

export function filterRepositoryItemsByMyAssignments<T extends RepositoryListItemLike>(
  items: T[],
  assignments: FileAssignmentRecord[],
  insideZip = false,
  listingContextPath = '',
  zipArchivePath = '',
  options?: { ignoreListingEntryGate?: boolean },
): T[] {
  if (!shouldFilterRepositoryByMyAssignments(assignments)) {
    return items;
  }
  return items.filter((item) =>
    isRepositoryItemAllowedByAssignments(
      item,
      assignments,
      insideZip,
      listingContextPath,
      zipArchivePath,
      options,
    ),
  );
}

function isStrictAncestorPath(ancestor: string, path: string): boolean {
  const a = normalizeRepoAssignmentPath(ancestor);
  const p = normalizeRepoAssignmentPath(path);
  if (!a || !p || a === p) {
    return false;
  }
  return p.startsWith(`${a}/`);
}

function isListingAtAssignmentEntry(listingPath: string, entryPath: string): boolean {
  if (!entryPath) {
    return false;
  }
  return pathsMatchForAssignment(listingPath, entryPath);
}

function repositoryRootPaths(): string[] {
  return [
    normalizeRepoAssignmentPath('/Repositorio'),
    normalizeRepoAssignmentPath('/home/egs/datavault/Repositorio'),
  ];
}

function assignmentNavigationPath(path: string): string {
  return normalizeRepoAssignmentPath(path);
}

/** Si la ruta actual está por encima del inicio acotado, devuelve el inicio. */
export function resolveAssignmentListingPathCorrection(
  currentPath: string,
  assignments: FileAssignmentRecord[],
): string | null {
  if (!shouldFilterRepositoryByMyAssignments(assignments)) {
    return null;
  }
  const home = resolveAssignmentScopedHomePath(assignments);
  if (!home) {
    return null;
  }
  const current = normalizeRepoAssignmentPath(currentPath);
  if (!current) {
    return home;
  }
  if (pathsMatchForAssignment(current, home)) {
    return null;
  }
  if (isStrictAncestorPath(current, home)) {
    return home;
  }
  if (!isRepositoryNavigationPathAllowed(current, assignments)) {
    return home;
  }
  return null;
}

/** Rutas donde el usuario asignado puede navegar (solo dentro de lo asignado). */
export function isRepositoryNavigationPathAllowed(
  path: string,
  assignments: FileAssignmentRecord[],
): boolean {
  if (!shouldFilterRepositoryByMyAssignments(assignments)) {
    return true;
  }
  const target = assignmentNavigationPath(path);
  if (!target) {
    return false;
  }

  const home = resolveAssignmentScopedHomePath(assignments);
  if (home && isStrictAncestorPath(target, home)) {
    return false;
  }

  for (const assignment of assignments) {
    if (!assignmentIsActive(assignment)) {
      continue;
    }
    const assigned = normalizeRepoAssignmentPath(assignment.file_path);
    if (!assigned) {
      continue;
    }
    const zipVirtual = splitZipVirtualAssignmentPath(assigned);

    if (zipVirtual) {
      if (pathsMatchForAssignment(target, zipVirtual.zipPath)) {
        return true;
      }
      const zipParent = parentDirectory(zipVirtual.zipPath);
      if (zipParent && target === zipParent) {
        return true;
      }
      if (home && target === home) {
        return true;
      }
      continue;
    }

    if (isAssignmentDirectoryRecord(assignment)) {
      if (isPathWithinDirectoryAssignmentScope(target, assigned, home)) {
        return true;
      }
      continue;
    }

    if (isPathWithinFileAssignmentScope(target, assigned, home)) {
      return true;
    }

    if (isZipArchiveAssignmentPath(assigned) && pathsMatchForAssignment(target, assigned)) {
      return true;
    }
  }

  return false;
}

/** Conserva vista dentro de un ZIP al refrescar o revalidar alcance. */
export function isRepositoryZipContextAllowed(
  zipPath: string,
  zipInternalPath: string,
  assignments: FileAssignmentRecord[],
): boolean {
  if (!shouldFilterRepositoryByMyAssignments(assignments)) {
    return true;
  }
  const zipNorm = normalizeRepoAssignmentPath(zipPath);
  if (!zipNorm || !isZipLikePath(zipNorm)) {
    return false;
  }
  const inner = normalizeZipInternalPath(zipInternalPath);

  for (const assignment of assignments) {
    if (!assignmentIsActive(assignment)) {
      continue;
    }
    const assigned = normalizeRepoAssignmentPath(assignment.file_path);
    if (!assigned) {
      continue;
    }
    const zipVirtual = splitZipVirtualAssignmentPath(assigned);
    if (zipVirtual) {
      if (!pathsMatchForAssignment(zipNorm, zipVirtual.zipPath)) {
        continue;
      }
      if (!inner) {
        return true;
      }
      if (isAssignmentDirectoryRecord(assignment)) {
        if (
          isZipInternalPathWithinAssignment(inner, zipVirtual.internalPath, true) ||
          inner === zipVirtual.internalPath ||
          inner.startsWith(`${zipVirtual.internalPath}/`)
        ) {
          return true;
        }
        continue;
      }
      if (isZipInternalPathWithinAssignment(inner, zipVirtual.internalPath, false)) {
        return true;
      }
      continue;
    }
    if (isZipLikePath(assigned) && pathsMatchForAssignment(zipNorm, assigned)) {
      return true;
    }
    /**
     * Carpeta DIP (u otra OAIS) asignada como directorio: listado ya muestra los .zip hijos,
     * pero al abrirlos el gate ZIP debe reconocer el mismo alcance de prefijo (Mantis #0000223).
     */
    if (isAssignmentDirectoryRecord(assignment)) {
      if (
        pathsMatchForAssignment(zipNorm, assigned) ||
        zipNorm.startsWith(`${assigned}/`) ||
        assignedPathContainsItem(assigned, zipNorm)
      ) {
        return true;
      }
    }
  }
  return false;
}

function longestCommonPathPrefix(paths: string[]): string {
  if (!paths.length) {
    return '';
  }
  const splitPaths = paths.map((p) => normalizeRepoAssignmentPath(p).split('/').filter(Boolean));
  const first = splitPaths[0] ?? [];
  let commonLength = 0;
  for (let i = 0; i < first.length; i++) {
    const segment = first[i];
    if (splitPaths.every((parts) => parts[i] === segment)) {
      commonLength += 1;
      continue;
    }
    break;
  }
  if (commonLength === 0) {
    return '';
  }
  return `/${first.slice(0, commonLength).join('/')}`;
}

/** Carpeta contenedora donde el usuario debe ver la asignación como fila navegable. */
export function resolveAssignmentEntryListPath(
  assignment: FileAssignmentRecord,
): string {
  const assigned = normalizeRepoAssignmentPath(assignment.file_path);
  if (!assigned) {
    return '';
  }
  const zipVirtual = splitZipVirtualAssignmentPath(assigned);
  if (zipVirtual) {
    return getParentRepositoryExplorerPath(zipVirtual.zipPath) || parentDirectory(zipVirtual.zipPath);
  }
  if (isAssignmentDirectoryRecord(assignment)) {
    return getParentRepositoryExplorerPath(assigned) || parentDirectory(assigned);
  }
  if (isZipArchiveAssignmentPath(assigned)) {
    return getParentRepositoryExplorerPath(assigned) || parentDirectory(assigned);
  }
  return getParentRepositoryExplorerPath(assigned) || parentDirectory(assigned);
}

/** Carpeta “inicio” para usuarios con asignación (Restaurar / primera carga). */
export function resolveUniqueAssignmentEntryPaths(
  assignments: FileAssignmentRecord[],
): string[] {
  const active = assignments.filter((row) => assignmentIsActive(row));
  const entryPaths = active
    .map((row) => resolveAssignmentEntryListPath(row))
    .filter(Boolean);
  return [...new Set(entryPaths)].sort((a, b) => a.localeCompare(b, 'es'));
}

/** Carpeta “inicio” para usuarios con asignación (Restaurar / primera carga). */
export function resolveAssignmentScopedHomePath(
  assignments: FileAssignmentRecord[],
  preferredActiveEntry?: string | null,
): string {
  const uniqueEntries = resolveUniqueAssignmentEntryPaths(assignments);
  if (!uniqueEntries.length) {
    return '';
  }
  if (uniqueEntries.length === 1) {
    return uniqueEntries[0]!;
  }

  const preferred = normalizeRepoAssignmentPath(preferredActiveEntry ?? '');
  if (preferred) {
    const match = uniqueEntries.find((entry) => pathsMatchForAssignment(entry, preferred));
    if (match) {
      return match;
    }
  }

  const commonEntries = longestCommonPathPrefix(uniqueEntries);
  const repoRoots = repositoryRootPaths();
  if (commonEntries && !repoRoots.some((root) => pathsMatchForAssignment(commonEntries, root))) {
    return commonEntries;
  }

  return uniqueEntries[0]!;
}

function isPathWithinDirectoryAssignmentScope(
  target: string,
  assigned: string,
  home: string,
): boolean {
  const parent = parentDirectory(assigned);
  if (target === parent || target === assigned) {
    return true;
  }
  if (target.startsWith(`${assigned}/`)) {
    return true;
  }
  if (home && target === home) {
    return true;
  }
  if (home && target.startsWith(`${home}/`) && assigned.startsWith(`${target}/`)) {
    return true;
  }
  return false;
}

function isPathWithinFileAssignmentScope(
  target: string,
  assigned: string,
  home: string,
): boolean {
  const parent = parentDirectory(assigned);
  if (target === assigned || pathsMatchForAssignment(target, assigned)) {
    return true;
  }
  if (target === parent || (home && target === home)) {
    return true;
  }
  return false;
}

export function resolveRepositoryFolderGoUpScoped(
  currentPath: string,
  assignments: FileAssignmentRecord[],
): string | null {
  if (!shouldFilterRepositoryByMyAssignments(assignments)) {
    return null;
  }
  const current = assignmentNavigationPath(currentPath);
  if (!current) {
    return null;
  }
  const idx = current.lastIndexOf('/');
  const parent = idx > 0 ? current.slice(0, idx) : '';
  if (!parent || !isRepositoryNavigationPathAllowed(parent, assignments)) {
    return null;
  }
  return parent;
}

export function resolveRepositoryZipGoUpScoped(
  zipPath: string,
  zipInternalPath: string,
  assignments: FileAssignmentRecord[],
): string | null {
  if (!shouldFilterRepositoryByMyAssignments(assignments)) {
    return normalizeZipInternalPath(zipInternalPath) ? getParentZipInternalPath(zipInternalPath) : null;
  }
  const current = normalizeZipInternalPath(zipInternalPath);
  // Ya en raíz del ZIP: Volver no sube (usar Salir ZIP).
  if (!current) {
    return null;
  }
  // Padre puede ser '' (raíz ZIP): p. ej. desde metadata/ o data/.
  const parentInternal = getParentZipInternalPath(current);
  if (isRepositoryZipContextAllowed(zipPath, parentInternal, assignments)) {
    return parentInternal;
  }
  return null;
}

export function canRepositoryTableNavBackScoped(
  ctx: {
    repositorySearchActive: boolean;
    currentZipPath: string | null;
    currentPath: string;
    zipInternalPath?: string;
  },
  assignments: FileAssignmentRecord[],
): boolean {
  if (!shouldFilterRepositoryByMyAssignments(assignments)) {
    return Boolean(ctx.currentPath?.trim()) || Boolean(ctx.currentZipPath);
  }
  if (ctx.repositorySearchActive) {
    return true;
  }
  if (ctx.currentZipPath) {
    return Boolean((ctx.zipInternalPath ?? '').trim());
  }
  return resolveRepositoryFolderGoUpScoped(ctx.currentPath, assignments) !== null;
}

/** @deprecated Usar resolveMyFileAssignmentsFromApi */
export function filterActiveAssignmentsForUser(
  assignments: FileAssignmentRecord[],
  userId: string,
): FileAssignmentRecord[] {
  return resolveMyFileAssignmentsFromApi(assignments, userId);
}
